"use client";

import React from "react";
import { ArrowRight, Droplets, RotateCcw, ShowerHead, Wrench, TriangleAlert as AlertTriangle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const problems = [{
  id: "faucet-leaking",
  title: "Faucet Leaking",
  badge: "Most Common",
  description: "Constant dripping or pooling water around the base, worn cartridges, high pressure, or failed internal washers.",
  path: "/faucet-sink-repair/",
  icon: Droplets
}, {
  id: "sink-drain",
  title: "Sink Won't Drain",
  badge: "Emergency Slowdown",
  description: "Standing water in kitchen or bathroom basins caused by grease buildup, soap scum, or foreign debris in the P-trap.",
  path: "/faucet-sink-repair/",
  icon: RotateCcw
}, {
  id: "shower-problems",
  title: "Shower Problems",
  badge: "Pressure & Valves",
  description: "Low water pressure, sudden temperature fluctuations, leaking shower heads, or faulty diverter valve mechanisms.",
  path: "/faucet-sink-repair/",
  icon: ShowerHead
}, {
  id: "disposal-not-working",
  title: "Garbage Disposal Not Working",
  badge: "Motor & Jam",
  description: "Humming without spinning, complete power loss, slow drainage, or water leaking directly beneath the unit.",
  path: "/garbage-disposal-repair/",
  icon: Wrench
}];
export default function FixtureProblems() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="fixtureproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div id="faucet-sink-problems" className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium">
              <AlertTriangle className="size-3.5 text-accent-foreground" />
              <span>Diagnostic Guide</span>
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Fixture Problems
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default">
            Quickly identify common fixture malfunctions in your kitchen or bathroom and connect directly with emergency repair services.
          </p>
        </div>

        {/* 4-Item Triage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {problems.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.id} data-index={i} className="h-full">
                <motion.div initial={shouldReduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-50px"
            }} transition={{
              duration: 0.4,
              delay: shouldReduceMotion ? 0 : i * 0.1
            }} whileHover={shouldReduceMotion ? {} : {
              y: -4
            }} className="h-full">
                  <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <IconComponent className="size-6" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-default text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="flex-1 pb-4">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>

                    <CardFooter className="pt-2">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between hover:bg-muted group">
                        <Link to={item.path} className="w-full flex items-center justify-between">
                          <span className="font-semibold text-primary">Diagnose & Fix</span>
                          <ArrowRight className="size-4 text-primary transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              </div>;
        })}
        </div>

        {/* Action Area */}
        <div className="text-center pt-4">
          <Button asChild size="lg" className="rounded-md px-8 py-6 text-base font-semibold shadow-md">
            <Link to="/faucet-sink-repair/">
              <span>View Fixture Problems</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}